import { useState, type FormEvent } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, Input, Switch } from '@/components/ui';
import {
  MAX_ALLOWED_IPS,
  kstDateOf,
  parseAllowedIps,
  periodFromParam,
  periodToParam,
} from '../lib/partnerFormat';
import type { OpenApiPartner, PartnerUpdatePayload } from '../types/openApiPartner';

const NAME_MAX = 100;
const TEXT_MAX = 500;

export interface PartnerFormProps {
  /** 수정이면 원본, 등록이면 undefined(사용 여부 토글 없음 — 등록은 항상 사용 중). */
  initial?: OpenApiPartner;
  submitting: boolean;
  /** BE 오류 메시지(400 검증 등). */
  errorMessage?: string | null;
  submitLabel: string;
  onSubmit: (payload: PartnerUpdatePayload) => void;
  onCancel?: () => void;
  /** OPS_ADMIN — 조회 전용. */
  readOnly?: boolean;
}

const textareaStyle = {
  background: 'var(--surface-0)',
  border: '1px solid var(--divider-strong)',
  color: 'var(--content-default)',
} as const;

/** 등록·수정 공용 폼. 사용기간은 KST 날짜 단위(시작일 00:00 ~ 종료일 23:59:59)로 보낸다. */
export const PartnerForm = ({
  initial,
  submitting,
  errorMessage,
  submitLabel,
  onSubmit,
  onCancel,
  readOnly = false,
}: PartnerFormProps) => {
  const { t } = useTranslation('common');
  const [name, setName] = useState(initial?.partnerName ?? '');
  const [ipsText, setIpsText] = useState((initial?.allowedIps ?? []).join('\n'));
  const [from, setFrom] = useState(kstDateOf(initial?.validFrom) ?? '');
  const [to, setTo] = useState(kstDateOf(initial?.validTo) ?? '');
  const [active, setActive] = useState(initial?.active ?? true);
  const [memo, setMemo] = useState(initial?.memo ?? '');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const { ips, invalid } = parseAllowedIps(ipsText);
  const nameError = name.trim() === '' ? t('openApiPartners.errors.nameRequired', '제휴사명을 입력하세요.') : null;
  const ipError =
    invalid.length > 0
      ? t('openApiPartners.errors.ipInvalid', {
          values: invalid.join(', '),
          defaultValue: '고정 IP 또는 /24 까지의 대역만 입력할 수 있습니다(IPv6 는 /64 까지, 도메인·* 불가): {{values}}',
        })
      : ips.length > MAX_ALLOWED_IPS
        ? t('openApiPartners.errors.ipTooMany', { max: MAX_ALLOWED_IPS, defaultValue: '허용 IP 는 최대 {{max}}개입니다.' })
        : null;
  const periodError =
    from && to && to < from ? t('openApiPartners.errors.periodOrder', '종료일이 시작일보다 빠릅니다.') : null;
  const hasError = Boolean(nameError || ipError || periodError);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (hasError || readOnly) return;
    onSubmit({
      partnerName: name.trim(),
      allowedIps: ips,
      validFrom: periodFromParam(from),
      validTo: periodToParam(to),
      active,
      memo: memo.trim() === '' ? null : memo.trim(),
      reason: reason.trim() === '' ? null : reason.trim(),
    });
  };

  const fieldError = (msg: string | null) =>
    touched && msg ? (
      <p className="text-[12px]" role="alert" style={{ color: 'var(--status-danger-fg)' }}>
        {msg}
      </p>
    ) : null;

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate data-testid="open-api-partner-form">
      <label className="grid gap-1 text-[13px]">
        <span style={{ color: 'var(--content-muted)' }}>
          {t('openApiPartners.fields.partnerName', '제휴사명')}
          <span style={{ color: 'var(--signal-danger)' }}> *</span>
        </span>
        <Input value={name} maxLength={NAME_MAX} disabled={readOnly} onChange={(e) => setName(e.target.value)} />
        {fieldError(nameError)}
      </label>

      <label className="grid gap-1 text-[13px]">
        <span style={{ color: 'var(--content-muted)' }}>{t('openApiPartners.fields.allowedIps', '허용 IP')}</span>
        <textarea
          className="min-h-[96px] rounded-md px-3 py-2 font-mono text-[13px]"
          style={textareaStyle}
          value={ipsText}
          disabled={readOnly}
          placeholder={'203.0.113.10\n203.0.113.0/24'}
          onChange={(e) => setIpsText(e.target.value)}
          aria-describedby="open-api-ips-hint"
        />
        <span id="open-api-ips-hint" className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
          {t('openApiPartners.hints.allowedIps', {
            max: MAX_ALLOWED_IPS,
            defaultValue:
              '한 줄에 하나씩 입력하세요. 예: 203.0.113.10 또는 203.0.113.0/24 (대역은 /24 까지, 최대 {{max}}개). IP 가 없으면 호출할 수 없습니다.',
          })}
        </span>
        {fieldError(ipError)}
      </label>

      <div className="grid gap-1 text-[13px]">
        <span style={{ color: 'var(--content-muted)' }}>{t('openApiPartners.fields.period', '사용기간')}</span>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            className="w-[160px]"
            value={from}
            disabled={readOnly}
            onChange={(e) => setFrom(e.target.value)}
            aria-label={t('openApiPartners.fields.validFrom', '사용 시작일')}
          />
          <span style={{ color: 'var(--content-muted)' }}>~</span>
          <Input
            type="date"
            className="w-[160px]"
            value={to}
            disabled={readOnly}
            onChange={(e) => setTo(e.target.value)}
            aria-label={t('openApiPartners.fields.validTo', '사용 종료일')}
          />
        </div>
        <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
          {t('openApiPartners.hints.period', '비우면 기간 제한이 없습니다. 날짜는 한국 시간 기준입니다.')}
        </span>
        {fieldError(periodError)}
      </div>

      {initial ? (
        <label className="flex items-center gap-2 text-[13px]">
          <Switch
            checked={active}
            disabled={readOnly}
            onCheckedChange={setActive}
            aria-label={t('openApiPartners.fields.active', '사용 여부')}
          />
          <span>
            {active
              ? t('openApiPartners.fields.activeOn', '사용 중')
              : t('openApiPartners.fields.activeOff', '사용 중지 (호출 불가)')}
          </span>
        </label>
      ) : null}

      <label className="grid gap-1 text-[13px]">
        <span style={{ color: 'var(--content-muted)' }}>{t('openApiPartners.fields.memo', '메모')}</span>
        <textarea
          className="min-h-[64px] rounded-md px-3 py-2 text-[13px]"
          style={textareaStyle}
          value={memo}
          maxLength={TEXT_MAX}
          disabled={readOnly}
          onChange={(e) => setMemo(e.target.value)}
        />
      </label>

      {readOnly ? null : (
        <label className="grid gap-1 text-[13px]">
          <span style={{ color: 'var(--content-muted)' }}>
            {initial ? t('openApiPartners.fields.changeReason', '변경 사유') : t('openApiPartners.fields.reason', '사유')}
          </span>
          <Input
            value={reason}
            maxLength={TEXT_MAX}
            placeholder={t('openApiPartners.hints.reason', '이력에 남습니다. (선택)')}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
      )}

      {errorMessage ? (
        <p role="alert" className="rounded px-3 py-2 text-[13px]" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
          {errorMessage}
        </p>
      ) : null}

      {readOnly ? null : (
        <div className="flex justify-end gap-2">
          {onCancel ? (
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
              {t('openApiPartners.actions.cancel', '취소')}
            </Button>
          ) : null}
          <Button type="submit" disabled={submitting}>
            {submitLabel}
          </Button>
        </div>
      )}
    </form>
  );
};
